import React, { FC, useEffect, useState } from 'react';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import moment from 'moment';
import { v4 as uuid } from 'uuid';

import { currencyToNumber, getDiffBetweenDatesInDays, numberToCurrency } from '@/utils/Formatter';
import { useToastErrorMessage } from '@/utils/Messages';

import { Property, PropertyGrouped } from '@/services/Property/types';
import {
  postExtendReservation,
  putFieldsOriginalReservation,
} from '@/services/Reservation/request';
import { Reservation } from '@/services/Reservation/types';

import { useLoader } from '@/context/LoaderContext';
import { useReservation } from '@/context/ReservationContext';
import { useToast } from '@/context/ToastContext';
import { useUser } from '@/context/UserContext';

import { useReservationForm } from '@/hooks/ReservationHook/useReservationForm';

import {
  DatePicker,
  FormButton,
  GenderSelect,
  RadioTypePerson,
  RadioYesOrNo,
  SimpleSelect,
  TextField,
} from '@/components/';
import { ContainerName } from '@/components/Calendar/Reservation/Modal/styles';
import {
  ButtonsContainer,
  Container,
  FormContainer,
  MediumText,
  MediumTextBold,
  TitleContainer,
  Wrapper,
} from '@/components/Calendar/Reservation/ModalGeneral/styles';
import FormAddress from '@/components/FormAddress';
import FormGridRow from '@/components/FormGridRow';
import RadioButton from '@/components/RadioButton';

import { FadeAnimation, FormContent, FormGridContainer, Space } from './styles';

interface Props {
  props?: object;
  extendReservation?: string | number;
  typePerson?: string;
  newExtensionContract?: boolean;
  onClose?: Function | boolean | any;
  extendOptions?: boolean;
  onExtend?: Function | boolean | any;
}

const ExtendReservation: FC<Props> = ({ onClose }) => {
  const { userInformation } = useUser();

  const { dataGrouped, setDataGrouped, data } = useReservationForm();
  const { reservationData, setReservationData } = useReservation();
  const propertyId = reservationData?.listing?.property?.id;
  const toastErrorRequest = useToastErrorMessage();
  const toast = useToast();

  const [checkOptions, setCheckOptions] = useState<boolean>();
  const [totalDays, setTotalDays] = useState<number>(0);

  const checkInSplitted = reservationData?.check_in_date?.split('/');
  const checkOutSplitted = reservationData?.check_out_date?.split('/');

  const { setLoad } = useLoader();

  const [radioCheckin, setRadioCheckin] = useState(false);

  let checkin = '';
  let checkout = '';
  let addCheckout = '';
  let addCheckin = '';
  if (checkInSplitted.length > 0) {
    const addCheckintDay = reservationData?.check_in_date;
    checkin = reservationData?.check_in_date;
    addCheckin = addCheckintDay.toString();
  }
  if (checkOutSplitted.length > 0) {
    const addCheckoutDay = moment(reservationData?.check_out_date).add(1, 'day');
    checkout = reservationData?.check_out_date;
    addCheckout = addCheckoutDay.toString();
  }

  let newExtension: Reservation;
  let extendReserveData: Reservation;

  const formik = useFormik({
    initialValues: {
      extendReservation: '0',
      newExtensionContract: false,
      extendOptions: true,
      typePerson: 'individual',
      ota: '',
      indication: '',
      corporateName: '',
      tradingName: '',
      phone1: '',
      phone2: '',
      guest: '',
      lastName: '',
      adults: 0,
      children: 0,
      guaranteeValue: numberToCurrency(reservationData?.guaranteeValue),
      totalPaid: numberToCurrency(data?.price),
      notes: '',
      pets: false,
      cpf: '',
      cnpj: '',
      rg: '',
      born: new Date(),
      gender: '',
      street: '',
      zipCode: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      country: 'BR',
      email: '',
      addCheckinDate: moment(new Date(addCheckin)).toDate(),
      addCheckoutDate: moment(new Date(addCheckout)).toDate(),
      newCheckInDate: !radioCheckin ? moment(checkin).toDate() : moment(checkout).toDate(),
      newCheckOutDate: !radioCheckin ? moment(checkin).toDate() : moment(checkout).toDate(),
    },
    onSubmit: async (values) => {
      setLoad(true);
      try {
        newExtension = {
          ...reservationData,
          property: reservationData?.listing?.property?.id,
          original_reservation: reservationData?.id,
          code: uuid(),
          total_price: currencyToNumber(formik.values.totalPaid),
          paid_amount: currencyToNumber(formik.values.totalPaid),
        };
        if (formik.values.extendReservation === '1') {
          setCheckOptions(true);
          newExtension = {
            ...newExtension,
            check_in_date: values.addCheckinDate.toString(),
            check_out_date: reservationData?.check_in_date,
            is_early_extension: true,
            is_late_extension: false,
          };
        }
        if (formik.values.extendReservation === '2') {
          setCheckOptions(false);
          newExtension = {
            ...newExtension,
            check_in_date: reservationData?.check_out_date,
            check_out_date: values.addCheckoutDate.toString(),
            is_early_extension: false,
            is_late_extension: true,
          };
        }
        if (formik.values.extendReservation === '3') {
          newExtension = {
            ...newExtension,
            check_in_date: !checkOptions ? reservationData?.check_out_date : values.newCheckInDate,
            check_out_date: checkOptions ? reservationData?.check_in_date : values.newCheckOutDate,
            is_early_extension: checkOptions ? true : reservationData?.is_early_extension,
            is_late_extension: !checkOptions ? true : reservationData?.is_late_extension,
          };
        }
        extendReserveData = await postExtendReservation(newExtension);
        let early;
        let late;
        if (formik.values.extendReservation === '1') {
          early = extendReserveData?.id;
        } else if (formik.values.extendReservation === '2') {
          late = extendReserveData?.id;
        } else if (
          checkOptions &&
          extendReserveData?.is_early_extension &&
          formik.values.extendReservation === '3'
        ) {
          early = reservationData?.early_extension_reservation
            ? reservationData?.early_extension_reservation
            : extendReserveData?.id;
        } else if (
          !checkOptions &&
          extendReserveData?.is_late_extension &&
          formik.values.extendReservation === '3'
        ) {
          late = reservationData?.late_extension_reservation
            ? reservationData?.late_extension_reservation
            : extendReserveData?.id;
        }

        let hasEarly;
        let hasLate;
        if (formik.values.extendReservation !== '3') {
          hasEarly = reservationData?.has_early_extension
            ? reservationData?.has_early_extension
            : false;
          hasLate = reservationData?.has_late_extension
            ? reservationData?.has_late_extension
            : false;
        } else if (checkOptions) {
          hasEarly = true;
          hasLate = reservationData?.has_late_extension !== false;
        } else if (!checkOptions) {
          hasEarly = reservationData?.has_early_extension !== false;
          hasLate = true;
        }

        const newReservationData = {
          ...reservationData,
          has_early_extension: formik.values.extendReservation === '1' ? true : hasEarly,
          has_late_extension: formik.values.extendReservation === '2' ? true : hasLate,
          early_extension_reservation: early,
          late_extension_reservation: late,
        };
        await putFieldsOriginalReservation(
          reservationData?.id,
          { ...reservationData },
          formik.values.extendReservation === '1' ? true : hasEarly,
          formik.values.extendReservation === '2' ? true : hasLate,
          early,
          late,
        );
        setReservationData({ ...newReservationData });
        let dataListing: PropertyGrouped[];
        if (dataGrouped) {
          dataListing = [...dataGrouped];
          dataListing.map((category: PropertyGrouped) =>
            category?.data.forEach((p: Property) => {
              const property = p;
              if (property !== null) {
                if (property?.id === propertyId && property?.reservation) {
                  Object.assign(property?.reservation, {
                    [extendReserveData.check_in_date]: {
                      ...newExtension,
                      id: extendReserveData?.id,
                    },
                  });
                  const newKey = reservationData?.check_in_date;
                  if (newKey in property?.reservation) {
                    if (checkOptions && formik.values.extendReservation === '3') {
                      property.reservation[newKey] = {
                        ...reservationData,
                        has_early_extension: true,
                        has_late_extension: reservationData?.has_late_extension
                          ? true
                          : reservationData?.has_late_extension,
                        early_extension_reservation: extendReserveData?.id,
                      };
                    } else if (!checkOptions && formik.values.extendReservation === '3') {
                      property.reservation[newKey] = {
                        ...reservationData,
                        has_early_extension: reservationData?.has_early_extension
                          ? true
                          : reservationData?.has_early_extension,
                        has_late_extension: reservationData?.has_late_extension
                          ? reservationData?.has_late_extension
                          : true,
                        late_extension_reservation: extendReserveData?.id,
                      };
                    } else if (formik.values.extendReservation === '2') {
                      property.reservation[newKey] = {
                        ...reservationData,
                        has_late_extension: true,
                        late_extension_reservation: extendReserveData?.id,
                      };
                    } else if (formik.values.extendReservation === '1') {
                      property.reservation[newKey] = {
                        ...reservationData,
                        has_early_extension: true,
                        late_extension_reservation: extendReserveData?.id,
                      };
                    }
                  }
                }
              }
            }),
          );
          setDataGrouped([...dataListing]);
        }
        toast.success('A reserva foi estendida com sucesso !');
        setLoad(false);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setLoad(false);
          toastErrorRequest(e);
        }
      }
    },
  });

  useEffect(() => {
    setCheckOptions(formik.values.extendOptions);
  }, [formik.values.extendOptions]);

  useEffect(() => {
    const total = getDiffBetweenDatesInDays(
      formik.values.newCheckInDate,
      formik.values.newCheckOutDate,
    );
    setTotalDays(total);
    if (total > 0) {
      formik?.setFieldValue(
        'totalPaid',
        numberToCurrency(currencyToNumber(formik.values.totalPaid) * total),
      );
      formik?.setFieldTouched('totalPaid', true, false);
    }
  }, [formik.values.newCheckInDate, formik.values.newCheckOutDate]);

  useEffect(() => {
    setTotalDays(1);
  }, [formik.values.addCheckoutDate, formik.values.addCheckinDate]);

  useEffect(() => {
    if (!radioCheckin) {
      formik?.setFieldValue('newCheckInDate', moment(checkin).toDate());
      formik?.setFieldTouched('newCheckInDate', true, false);
      formik?.setFieldValue('newCheckOutDate', moment(checkin).toDate());
      formik?.setFieldTouched('newCheckOutDate', true, false);
    } else {
      formik?.setFieldValue('newCheckInDate', moment(checkout).toDate());
      formik?.setFieldTouched('newCheckInDate', true, false);
      formik?.setFieldValue('newCheckOutDate', moment(checkout).toDate());
      formik?.setFieldTouched('newCheckOutDate', true, false);
    }
  }, [radioCheckin]);

  return (
    <Container role={userInformation?.main_role || ''}>
      <FormContainer>
        <FormContent onSubmit={formik.handleSubmit}>
          <TitleContainer>
            <Wrapper>
              <MediumTextBold>Estender Reserva</MediumTextBold>
            </Wrapper>
          </TitleContainer>
          <FormGridRow>
            <FormGridContainer>
              <Wrapper>
                {(reservationData?.has_late_extension === true &&
                  reservationData?.has_early_extension === false) ||
                reservationData?.is_early_extension ? (
                  <SimpleSelect
                    id="extendReservation"
                    placeholder="Selecione"
                    label="Tipo"
                    disabled={false}
                    formik={formik}
                    options={[
                      { value: '1', valueLabel: 'Early Checkin' },
                      { value: '3', valueLabel: 'Extensão' },
                    ]}
                  />
                ) : (
                  <>
                    {(reservationData?.has_early_extension === true &&
                      !reservationData?.has_late_extension) ||
                    reservationData?.is_late_extension ? (
                      <SimpleSelect
                        id="extendReservation"
                        placeholder="Selecione"
                        label="Tipo"
                        disabled={false}
                        formik={formik}
                        options={[
                          { value: '2', valueLabel: 'Late Checkout' },
                          { value: '3', valueLabel: 'Extensão' },
                        ]}
                      />
                    ) : (
                      <>
                        {reservationData?.has_early_extension === false &&
                        reservationData?.has_late_extension === false ? (
                          <SimpleSelect
                            id="extendReservation"
                            placeholder="Selecione"
                            label="Tipo"
                            disabled={false}
                            formik={formik}
                            options={[
                              { value: '1', valueLabel: 'Early Checkin' },
                              { value: '2', valueLabel: 'Late Checkout' },
                              { value: '3', valueLabel: 'Extensão' },
                            ]}
                          />
                        ) : (
                          <>
                            {reservationData?.has_early_extension === true &&
                            reservationData?.has_late_extension === true ? (
                              <div />
                            ) : (
                              <div />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Wrapper>
            </FormGridContainer>
          </FormGridRow>

          {formik.values.extendReservation === '1' ? (
            <FadeAnimation>
              <DatePicker
                hidden
                label="Selecione a nova data de check-in"
                id="addCheckinDate"
                formik={formik}
                minDate={new Date('1850-01-02')}
              />
              <Wrapper>
                <MediumTextBold>Pagamento adicional de extensão</MediumTextBold>
              </Wrapper>
              <Space />
              <FormGridRow grid>
                <Grid item sm={6} xs={12}>
                  <TextField
                    label="Valor"
                    id="totalPaid"
                    formik={formik}
                    mask="money"
                    placeholder="R$: 0,00"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <SimpleSelect
                    id="extendReservation"
                    placeholder="Selecione"
                    label="Forma de pagamento"
                    disabled={false}
                    options={[
                      { value: '1', valueLabel: 'Cartão de Crédito' },
                      { value: '2', valueLabel: 'Boleto Bancário' },
                      { value: '3', valueLabel: 'Paypal' },
                    ]}
                  />
                </Grid>
              </FormGridRow>
              <Space />
              <TextField label="Notas" id="notes" type="textarea" />
              <Space />
            </FadeAnimation>
          ) : (
            <div />
          )}

          {formik.values.extendReservation === '2' ? (
            <FadeAnimation>
              <DatePicker
                hidden
                label="Selecione a nova data de check-in"
                id="addCheckoutDate"
                formik={formik}
                minDate={new Date('1850-01-02')}
              />
              <Wrapper>
                <MediumTextBold>Pagamento adicional de extensão</MediumTextBold>
              </Wrapper>
              <Space />
              <FormGridRow grid>
                <Grid item sm={6} xs={12}>
                  <TextField
                    label="Valor"
                    id="totalPaid"
                    formik={formik}
                    mask="money"
                    placeholder="R$: 0,00"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <SimpleSelect
                    id="extendReservation"
                    placeholder="Selecione"
                    label="Forma de pagamento"
                    disabled={false}
                    options={[
                      { value: '1', valueLabel: 'Cartão de Crédito' },
                      { value: '2', valueLabel: 'Boleto Bancário' },
                      { value: '3', valueLabel: 'Paypal' },
                    ]}
                  />
                </Grid>
              </FormGridRow>
              <Space />
              <TextField label="Notas" id="notes" type="textarea" />
              <Space />
            </FadeAnimation>
          ) : (
            <div />
          )}

          {formik.values.extendReservation === '3' ? (
            <FadeAnimation>
              {(reservationData?.has_late_extension === true &&
                reservationData?.has_early_extension === false) ||
              reservationData?.is_early_extension ? (
                <RadioButton
                  id="extendOptions"
                  labelPlacement="end"
                  formLabel="Extensão para fazer: "
                  onClick={() => setRadioCheckin(formik.values.extendOptions)}
                  formik={formik}
                  options={[{ value: true, label: 'Check-in' }]}
                />
              ) : (
                <>
                  {(reservationData?.has_early_extension === true &&
                    reservationData?.has_late_extension === false) ||
                  reservationData?.is_late_extension ? (
                    <RadioButton
                      id="extendOptions"
                      labelPlacement="end"
                      formLabel="Extensão para fazer: "
                      formik={formik}
                      onClick={() => setRadioCheckin(formik.values.extendOptions)}
                      options={[{ value: false, label: 'Check-out' }]}
                    />
                  ) : (
                    <>
                      {reservationData?.has_early_extension === false &&
                      reservationData?.has_late_extension === false ? (
                        <RadioButton
                          id="extendOptions"
                          labelPlacement="end"
                          formLabel="Extensão para fazer: "
                          formik={formik}
                          onClick={() => setRadioCheckin(formik.values.extendOptions)}
                          options={[
                            { value: true, label: 'Check-in' },
                            { value: false, label: 'Check-out' },
                          ]}
                        />
                      ) : (
                        <>
                          {reservationData?.has_early_extension === true &&
                          reservationData?.has_late_extension === true ? (
                            <div />
                          ) : (
                            <div />
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              <Space />
              {formik.values.extendOptions ? (
                <DatePicker
                  label="Selecione a nova data de check-in"
                  id="newCheckInDate"
                  formik={formik}
                  minDate={new Date('1850-01-02')}
                />
              ) : (
                <DatePicker
                  label="Selecione a nova data de check-out"
                  id="newCheckOutDate"
                  formik={formik}
                  minDate={new Date('1850-01-02')}
                />
              )}
              <Space />
              <MediumText>Total de {totalDays} noites</MediumText>
              <Space />
              <FadeAnimation>
                <Wrapper>
                  <MediumTextBold>Pagamento adicional de extensão</MediumTextBold>
                </Wrapper>
                <Space />
                <FormGridRow grid>
                  <Grid item sm={6} xs={12}>
                    <TextField
                      label="Valor"
                      id="totalPaid"
                      formik={formik}
                      mask="money"
                      placeholder="R$: 0,00"
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <SimpleSelect
                      id="extendReservation"
                      placeholder="Selecione"
                      label="Forma de pagamento"
                      disabled={false}
                      options={[
                        { value: '1', valueLabel: 'Cartão de Crédito' },
                        { value: '2', valueLabel: 'Boleto Bancário' },
                        { value: '3', valueLabel: 'Paypal' },
                      ]}
                    />
                  </Grid>
                </FormGridRow>
                <FadeAnimation>
                  <FadeAnimation>
                    <Space />
                    <RadioYesOrNo
                      id="newExtensionContract"
                      formLabel="Gerar novo contrato ?"
                      labelPlacement="end"
                      formik={formik}
                    />
                    <Space />
                    {formik.values.newExtensionContract === true ? (
                      <FadeAnimation>
                        <ContainerName>
                          <FormGridRow>
                            <TextField label="*Nome" id="guest" formik={formik} />
                          </FormGridRow>

                          <FormGridRow>
                            <TextField label="*Sobrenome" id="lastName" formik={formik} />
                          </FormGridRow>

                          <FormGridRow>
                            <TextField label="*E-mail" id="email" formik={formik} />
                          </FormGridRow>

                          <FormGridRow grid>
                            <Grid item sm={6} xs={12}>
                              <TextField
                                label="*Telefone Principal"
                                placeholder="+00 (00) 00000-0000"
                                id="phone1"
                                formik={formik}
                                mask="phone"
                              />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <TextField
                                label="Telefone Secundário"
                                placeholder="+00 (00) 00000-0000"
                                id="phone2"
                                formik={formik}
                                mask="phone"
                              />
                            </Grid>
                          </FormGridRow>

                          <FormGridRow grid>
                            <Grid item sm={6} xs={12}>
                              <GenderSelect formik={formik} />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <DatePicker
                                formik={formik}
                                label="*Data de nasc."
                                id="born"
                                minDate={new Date('1850-01-02')}
                              />
                            </Grid>
                          </FormGridRow>

                          <FormGridRow>
                            <RadioTypePerson labelPlacement="end" id="typePerson" formik={formik} />
                          </FormGridRow>

                          {formik.values.typePerson === 'legal' && (
                            <>
                              <FormGridRow grid>
                                <Grid item sm={6} xs={12}>
                                  <TextField label="*CNPJ" formik={formik} id="cnpj" mask="cnpj" />
                                </Grid>
                              </FormGridRow>

                              <FormGridRow grid>
                                <Grid item sm={6} xs={12}>
                                  <TextField
                                    label="Razão Social"
                                    id="corporateName"
                                    formik={formik}
                                  />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                  <TextField
                                    label="Nome Fantasia"
                                    id="tradingName"
                                    formik={formik}
                                  />
                                </Grid>
                              </FormGridRow>
                            </>
                          )}
                          <FormGridRow grid>
                            <Grid item sm={6} xs={12}>
                              <TextField label="*CPF" id="cpf" formik={formik} mask="cpf" />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <TextField label="*Nº de identidade" formik={formik} id="rg" />
                            </Grid>
                          </FormGridRow>
                          <FormAddress formik={formik} />
                          <Space />
                        </ContainerName>
                      </FadeAnimation>
                    ) : (
                      <div />
                    )}
                  </FadeAnimation>
                </FadeAnimation>
                <Space />
                <TextField label="Notas" id="notes" formik={formik} type="textarea" />
                <Space />
              </FadeAnimation>
              <Space />
            </FadeAnimation>
          ) : (
            <div />
          )}
          <ButtonsContainer>
            <FormButton type="button" onClick={() => onClose(false)} variant="outlined">
              Voltar
            </FormButton>
            <FormButton type="submit">Salvar</FormButton>
            <MoreVertIcon />
          </ButtonsContainer>
        </FormContent>
      </FormContainer>
    </Container>
  );
};

export default ExtendReservation;
